import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CancellationMotive, InitiatedBy } from "./types";
import apiCoreAdapter from "../../../hooks/apiCoreAdapter";

export interface RescheduleReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** initiatedBy se envía al back en formato API ('psychologist' | 'patient'). */
  onConfirm: (motiveId: string, note: string | undefined, initiatedBy: 'psychologist' | 'patient') => void;
  /** Mientras se procesa la reprogramación (cancelar + reagendar). */
  confirming?: boolean;
  patientName?: string;
}

/**
 * Modal para elegir el motivo de una reprogramación, con el mismo estilo que el
 * paso de motivo del wizard de cancelación (VariantWizard). Un reagendamiento
 * cancela la sesión original con `cancellation_type: 'rescheduled'`; este modal
 * permite además registrar el motivo real (motive_id) y un comentario, para
 * trackear bien por qué se reprograman las sesiones.
 *
 * El reagendamiento desde psi-platform siempre lo inicia la psicóloga, así que
 * mostramos los motivos aplicables a 'psychologist' (más los condicionales que
 * derivan a un motivo secundario, igual que en la cancelación).
 */
export function RescheduleReasonModal({
  isOpen,
  onClose,
  onConfirm,
  confirming,
  patientName,
}: RescheduleReasonModalProps) {
  const [selectedMotive, setSelectedMotive] = useState<CancellationMotive | null>(null);
  const [secondaryMotive, setSecondaryMotive] = useState<CancellationMotive | null>(null);
  const [note, setNote] = useState("");
  const [motives, setMotives] = useState<CancellationMotive[]>([]);

  useEffect(() => {
    if (isOpen) {
      apiCoreAdapter.getCancellationMotives().then(setMotives).catch(() => setMotives([]));
    }
  }, [isOpen]);

  const psychologistMotives = motives.filter(
    (m) => m.applies_to === 'psychologist' || m.applies_to === 'both'
  );
  const patientMotives = motives.filter(
    (m) => m.applies_to === 'patient' || m.applies_to === 'both'
  );

  const primaryMotives = [...psychologistMotives].sort((a, b) => {
    const weight = (name: string) => (name === 'OTHER' ? 2 : name === 'PATIENT_ASKED' ? 1 : 0);
    return weight(a.name) - weight(b.name);
  });

  const isConditional = selectedMotive?.action === 'show_secondary';

  const secondaryMotives = (
    selectedMotive?.name === 'PSYCHOLOGIST_ASKED'
      ? psychologistMotives.filter((m) => m.action !== 'show_secondary')
      : patientMotives.filter((m) => m.action !== 'show_secondary')
  ).sort((a, b) => {
    if (a.name === 'OTHER') return 1;
    if (b.name === 'OTHER') return -1;
    return 0;
  });

  const effectiveInitiatedBy: InitiatedBy =
    selectedMotive?.name === 'PSYCHOLOGIST_ASKED' ? 'Psicóloga' :
    selectedMotive?.name === 'PATIENT_ASKED' ? 'Paciente' :
    'Psicóloga';

  const activeMotive = isConditional ? secondaryMotive : selectedMotive;
  const canConfirm = !!activeMotive && note.trim().length > 0 && !confirming;

  const resetState = () => {
    setSelectedMotive(null);
    setSecondaryMotive(null);
    setNote("");
  };

  const handleClose = () => {
    if (confirming) return;
    resetState();
    onClose();
  };

  const handleConfirm = () => {
    if (!activeMotive) return;
    const initiatedByApi = effectiveInitiatedBy === 'Psicóloga' ? 'psychologist' : 'patient';
    onConfirm(activeMotive.id, note || undefined, initiatedByApi);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 rounded-2xl bg-white border-none shadow-xl">
        <DialogHeader className="px-6 pt-6 pb-0 space-y-1">
          <DialogTitle className="text-xl font-semibold text-foreground">
            Motivo de la reprogramación
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {patientName
              ? `Seleccioná por qué reprogramás la sesión con ${patientName}.`
              : 'Seleccioná el motivo de la reprogramación.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4">
          {/* Selector de motivo principal */}
          <Select
            value={selectedMotive?.id ?? ""}
            onValueChange={(id) => {
              const motive = primaryMotives.find((m) => m.id === id) ?? null;
              setSelectedMotive(motive);
              setSecondaryMotive(null);
              setNote("");
            }}
          >
            <SelectTrigger className="w-full rounded-full border-primary/30 bg-white text-primary placeholder:text-primary/60 focus:ring-primary/30">
              <SelectValue placeholder="Seleccionar motivo" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {primaryMotives.map((m) => (
                <SelectItem key={m.id} value={m.id} className="rounded-lg">
                  {m.description || m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Selector secundario para caso condicional */}
          {isConditional && (
            <>
              <p className="text-xs text-muted-foreground">
                {selectedMotive?.name === 'PSYCHOLOGIST_ASKED'
                  ? '¿Por qué pidió reprogramar la psicóloga?'
                  : '¿Por qué pidió reprogramar el paciente?'}
              </p>
              <Select
                value={secondaryMotive?.id ?? ""}
                onValueChange={(id) => {
                  const motive = secondaryMotives.find((m) => m.id === id) ?? null;
                  setSecondaryMotive(motive);
                  setNote("");
                }}
              >
                <SelectTrigger className="w-full rounded-full border-primary/30 bg-white text-primary placeholder:text-primary/60 focus:ring-primary/30">
                  <SelectValue placeholder="Seleccionar motivo" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {secondaryMotives.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="rounded-lg">
                      {m.description || m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          {/* Nota obligatoria */}
          {(isConditional ? !!secondaryMotive : !!selectedMotive) && (
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Agregá un comentario (obligatorio)"
              className="min-h-[100px] resize-none rounded-xl border-primary/30 bg-white focus-visible:ring-primary/30"
            />
          )}
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <Button
            variant="outline"
            className="flex-1 rounded-full border-border hover:bg-muted/50"
            onClick={handleClose}
            disabled={confirming}
          >
            Atrás
          </Button>
          <Button
            className="flex-1 rounded-full bg-primary hover:bg-primary/90"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {confirming ? 'Reprogramando…' : 'Continuar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RescheduleReasonModal;
